import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useTheme, useLanguage, API } from '@/App';
import { getTranslation } from '@/translations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import ThemeLanguageToggle from '@/components/ThemeLanguageToggle';
import GoogleAuthButton from '@/components/GoogleAuthButton';
import { Mail, Lock, User, Eye, EyeOff, ArrowRight, Check, Shield } from 'lucide-react';
import axios from 'axios';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const RegisterPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Load and render Turnstile widget
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    
    let widgetId = null;
    
    const renderTurnstile = () => {
      if (turnstileRef.current && window.turnstile) {
        // Clear any existing widget first
        turnstileRef.current.innerHTML = '';
        
        widgetId = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: theme === 'dark' ? 'dark' : 'light',
          callback: (token) => {
            setTurnstileToken(token);
          },
          'expired-callback': () => {
            setTurnstileToken('');
          },
          'error-callback': () => {
            setTurnstileToken('');
          }
        });
      }
    };
    
    // Check if Turnstile script is already loaded
    if (window.turnstile) {
      // Script already loaded, render immediately
      renderTurnstile();
    } else {
      // Script not loaded yet, load it
      const existingScript = document.getElementById('turnstile-script');
      if (!existingScript) {
        const script = document.createElement('script');
        script.id = 'turnstile-script';
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
        script.async = true;
        script.onload = () => {
          renderTurnstile();
        };
        document.head.appendChild(script);
      } else {
        // Script exists but turnstile not ready, wait for it
        const checkTurnstile = setInterval(() => {
          if (window.turnstile) {
            clearInterval(checkTurnstile);
            renderTurnstile();
          }
        }, 100);
        
        // Clear interval after 10 seconds to prevent infinite loop
        setTimeout(() => clearInterval(checkTurnstile), 10000);
      }
    }
    
    return () => {
      // Cleanup widget on unmount
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch (e) { console.error('Turnstile cleanup failed:', e); }
      }
      setTurnstileToken('');
    };
  }, [theme]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password length
    if (password.length < 8) {
      toast.error('Password minimal 8 karakter');
      return;
    }
    
    // Validate name length
    if (name.trim().length < 2) {
      toast.error('Nama minimal 2 karakter');
      return;
    }
    
    // Check Turnstile token
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      toast.error('Mohon selesaikan verifikasi keamanan');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email,
        password,
        name: name.trim(),
        turnstile_token: turnstileToken
      });
      
      if (response.data.success) {
        // Redirect to verification pending page
        navigate('/verify-pending', { state: { email } });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Registrasi gagal. Silakan coba lagi.';
      toast.error(errorMessage);
      
      // Reset Turnstile on error
      if (window.turnstile && turnstileRef.current) {
        window.turnstile.reset(turnstileRef.current);
        setTurnstileToken('');
      };
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A] flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-500 to-indigo-600 p-12 flex-col justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src="/images/logo-white-bg.png" alt="Nerava" className="w-10 h-10 rounded-lg" />
          <span className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>Nerava</span>
        </Link>

        <div>
          <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
            {t('auth.startJourney')}
          </h1>
          <p className="text-white/80 text-lg mb-8">
            {t('auth.joinBusiness')}
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-white/90">
              <Check className="w-5 h-5" />
              <span>{t('auth.benefit1')}</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <Check className="w-5 h-5" />
              <span>{t('auth.benefit2')}</span>
            </div>
            <div className="flex items-center gap-3 text-white/90">
              <Check className="w-5 h-5" />
              <span>{t('auth.benefit3')}</span>
            </div>
          </div>
        </div>

        <div className="text-white/60 text-sm">
          © 2026 Nerava.id. All rights reserved.
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 p-8 flex items-center justify-center bg-white dark:bg-zinc-900">
        <div className="w-full max-w-md">
          <div className="mb-4 flex justify-end">
            {/* <ThemeLanguageToggle /> */}
          </div>
          
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Link to="/" className="flex items-center gap-2">
              <img src="/images/logo.png" alt="Nerava" className="w-10 h-10" />
              <span className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50" style={{ fontFamily: 'Manrope' }}>Nerava</span>
            </Link>
          </div>

          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 mb-2" style={{ fontFamily: 'Manrope' }}>
            {t('auth.registerTitle')}
          </h2>
          <p className="text-slate-600 dark:text-zinc-400 mb-8">
            {t('auth.createAccount')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-slate-700 dark:text-zinc-300 mb-2 block">{t('auth.name')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 dark:text-zinc-400" />
                <Input
                  id="name"
                  type="text"
                  placeholder={t('auth.enterName')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 py-6 rounded-xl bg-white dark:bg-zinc-800 border-slate-300 dark:border-zinc-700 text-slate-900 dark:text-zinc-50"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-700 dark:text-zinc-300 mb-2 block">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 dark:text-zinc-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t('auth.enterEmail')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 py-6 rounded-xl bg-white dark:bg-zinc-800 border-slate-300 dark:border-zinc-700 text-slate-900 dark:text-zinc-50"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700 dark:text-zinc-300 mb-2 block">{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 dark:text-zinc-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('auth.enterPassword')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 py-6 rounded-xl bg-white dark:bg-zinc-800 border-slate-300 dark:border-zinc-700 text-slate-900 dark:text-zinc-50"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 dark:text-zinc-400"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-sm text-slate-500 dark:text-zinc-400 mt-1">
                Minimal 8 karakter
              </p>
            </div>

            {/* Cloudflare Turnstile Widget */}
            {TURNSTILE_SITE_KEY && (
              <div className="flex flex-col items-center gap-2">
                <div ref={turnstileRef} data-testid="turnstile-widget"></div>
                {turnstileToken && (
                  <p className="text-xs text-indigo-600 flex items-center gap-1 dark:text-indigo-400">
                    <Shield className="w-3 h-3" />
                    Verifikasi keamanan berhasil
                  </p>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || (TURNSTILE_SITE_KEY && !turnstileToken)}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-6 rounded-xl disabled:opacity-50"
              data-testid="register-btn"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  {t('auth.register')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-slate-200 dark:bg-zinc-800" />
            <span className="text-xs uppercase tracking-wider text-slate-500 dark:text-zinc-500">{t('auth.orDivider')}</span>
            <div className="h-px flex-1 bg-slate-200 dark:bg-zinc-800" />
          </div>

          <GoogleAuthButton label={t('auth.continueWithGoogle')} testId="google-register-btn" />

          <p className="text-center text-slate-600 dark:text-zinc-400 mt-8">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link to="/login" className="text-indigo-600 font-medium hover:underline dark:text-indigo-400" data-testid="login-link">
              {t('auth.login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
